import React, { createContext, useState, useContext, useCallback } from 'react';
import api from '../services/api';
import { useLoader } from './LoaderContext';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

// Create the context
const ProjectContext = createContext();

// Custom hook to use the context
export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

// Mock developers data
const mockDevelopers = [
  { id: '1', name: 'ABC Builders', description: 'Premium real estate developer', website: 'https://abcbuilders.com' },
  { id: '2', name: 'XYZ Developers', description: 'Luxury home builders', website: 'https://xyzdevelopers.com' },
  { id: '3', name: 'City Homes', description: 'Urban housing specialists', website: 'https://cityhomes.com' },
  { id: '4', name: 'Green Estates', description: 'Eco-friendly property developers', website: 'https://greenestates.com' }
];

export const ProjectProvider = ({ children }) => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  // State for projects list
  const [projects, setProjects] = useState([]);
  const [projectData, setProjectData] = useState(null);
  const [configurations, setConfigurations] = useState([]);
  const [configurationData, setConfigurationData] = useState(null);
  const [developers, setDevelopers] = useState(mockDevelopers);
  const [configutationUnits, setConfigutationUnits] = useState([]);
  const [unitData, setUnitData] = useState(null);

  // State for current project details
  const [currentProject, setCurrentProject] = useState(null);

  const [filterOptions, setFilterOptions] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [defaultFilterOptions, setDefaultFilterOptions] = useState({});
  const [selectionOptions, setSelectionOptions] = useState({});

  // Add these to your ProjectContext state and methods
  const [currentUnit, setCurrentUnit] = useState(null);

  // Fetch projects with applied filters and pagination
  const fetchProjects = async (params = {}) => {    
    showLoader();
    try {
      // Extract pagination and filter parameters
      const { page = 1, page_size = 50, ...filters } = params;
      
      // Determine the date range to use
      let startDate;
      let endDate;

      if (params.dateRange && params.dateRange.startDate && params.dateRange.endDate) {
        // Use provided date range if valid
        startDate = params.dateRange.startDate;
        endDate = params.dateRange.endDate;
      } else {
        endDate = dayjs(); // Today
        startDate = endDate.subtract(30, 'day');
      }

      // Create request payload with filters and pagination
      const payload = {
        // Include pagination
        page: page,
        page_size: page_size,
        // Include date range
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
        // Include all other filters
        ...filters
      };

      console.log('Fetching projects with payload:', payload);

      // Use POST request for filtering
      const response = await api.post('/lms/projects/', payload);
      const projectsData = response.data;
      setProjects(projectsData);
      return projectsData;
    } catch (error) {
      console.error('Error fetching projects:', error);
      return [];
    } finally {
      hideLoader();
    }
  };
  
  /**
   * Fetch details of a specific project
   * @param {number} projectId - The ID of the project to fetch
   */
  const fetchProjectData = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      const response = await api.get(`/lms/projects/${projectId}/`);
      setProjectData(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw error;
    }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await api.get('/lms/projects/filters/');
      setFilterOptions(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching filter options:', error);
      return {};
    } finally {
      hideLoader();
    }
  }, []);

  const fetchDefaultFilterOptions = useCallback(async () => {
    showLoader();
    try {
      const response = await api.get('/lms/projects/filters/default/');
      setDefaultFilterOptions(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching default filter options:', error);
      return {};
    } finally {
      hideLoader();
    }
  }, []);

  // Mock fetchDevelopers function
  const fetchDevelopers = async () => {
    showLoader();
    try{
      const response = await api.get('/lms/developers/');
      setDevelopers(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching developers:', error);
      return [];
    } finally {
      hideLoader();
    }
  };

  // Mock createProject function
  const createProject = async (projectData) => {
    try{
      const response = await api.post('/lms/projects/add-new/', projectData);
      navigate('/projects');
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  };

  const updateProjectData = async (projectId, changedData) => {
    try {
      // For partial updates, some APIs prefer PATCH over PUT
      const response = await api.patch(`/lms/projects/${projectId}/`, changedData);
      // Update the local project data with the response
      setProjectData(response.data);
      return response;
    } catch (error) {
      console.error('Error updating project data:', error);
      throw error;
    }
  };

  const deleteProject = useCallback(async (projectId) => {
    showLoader();
    try {
      const response = await api.delete(`/lms/projects/${projectId}/`);
      return response.data;    
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    } finally {
      hideLoader();
    }
  }, []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setAppliedFilters({});
    fetchProjects({});
  }, []);

  const fetchSelectionOptions = useCallback(async () => {
    try {
      const response = await api.get('/lms/projects/selection-options/');
      setSelectionOptions(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching selection options:', error);
      throw error;
    }
  }, []);

  const fetchConfigurations = async (projectId) => {
    showLoader();
    try {
      const response = await api.get(`/lms/projects/${projectId}/configurations/`);
      setConfigurations(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching configurations:', error);
      return [];
    } finally {
      hideLoader();
    } 
  };

  const fetchConfiguration = useCallback(async (projectId, configId) => {
    showLoader();
    try {
      const response = await api.get(`/lms/projects/${projectId}/configurations/${configId}/`);
      setConfigurationData(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching configuration:', error);
      throw error;
    } finally {
      hideLoader();
    }
  }, []);

  const createConfiguration = async (projectId, data) => {
    try {
      const response = await api.post(`/lms/projects/${projectId}/configurations/create/`, {
        ...data,
        project: projectId,
      });
      
      // Update configurations state
      setConfigurations([...configurations, response.data]);
      
      // Update projectData if it exists and matches the projectId
      if (projectData && projectData.id === projectId) {
        setProjectData({
          ...projectData,
          configurations: [...(projectData.configurations || []), response.data]
        });
      }
      
      // Force a refresh of project data to ensure consistency
      try {
        const refreshedProjectData = await fetchProjectData(projectId);
        setProjectData(refreshedProjectData);
      } catch (refreshError) {
        console.warn('Failed to refresh project data after configuration creation:', refreshError);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating configuration:', error);
      throw error;
    }
  };

  const updateConfiguration = async (projectId, configId, data) => {
    try {
      const response = await api.put(`/lms/projects/${projectId}/configurations/${configId}/`, data);
      
      // Update configurations state
      setConfigurations(configurations.map(config => 
        config.id === configId ? response.data : config
      ));
      
      // Update projectData if it exists and matches the projectId
      if (projectData && projectData.id === projectId) {
        setProjectData({
          ...projectData,
          configurations: (projectData.configurations || []).map(config => 
            config.id === configId ? response.data : config
          )
        });
      }
      
      // Force a refresh of project data to ensure consistency
      try {
        const refreshedProjectData = await fetchProjectData(projectId);
        setProjectData(refreshedProjectData);
      } catch (refreshError) {
        console.warn('Failed to refresh project data after configuration update:', refreshError);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating configuration:', error);
      throw error;
    }
  };

  const deleteConfiguration = async (projectId, configId) => {
    try {
      const response = await api.delete(`/lms/projects/${projectId}/configurations/${configId}/`);
      
      // Update configurations state
      setConfigurations(configurations.filter(config => config.id !== configId));
      
      // Update projectData if it exists and matches the projectId
      if (projectData && projectData.id === projectId) {
        setProjectData({
          ...projectData,
          configurations: (projectData.configurations || []).filter(config => config.id !== configId)
        });
      }
      
      // Force a refresh of project data to ensure consistency
      try {
        const refreshedProjectData = await fetchProjectData(projectId);
        setProjectData(refreshedProjectData);
      } catch (refreshError) {
        console.warn('Failed to refresh project data after configuration deletion:', refreshError);
      }
      return response;
    } catch (error) {
      console.error('Error deleting configuration:', error);
      throw error;
    }
  };

  const uploadAttachment = async (type, payload) => {
    try {
      console.log('uploadAttachment', type, payload);
      const { itemId, files, upload_type, upload_key } = payload;
      
      if (!itemId || !upload_type || !files || !Array.isArray(files)) {
        throw new Error('Invalid payload. Expected { itemId, uploadType, files: [File] }');
      }
      
      const formData = new FormData();
      formData.append('upload_key', upload_key); // 'brochure', 'floor_plans', etc.
      formData.append('upload_type', upload_type); // 'project' or 'configuration'
      let file_keys = [];
      // Append files with their file_keys as separate form fields
      files.forEach((file, index) => {
        formData.append('files', file);
        file_keys.push(file.file_key);
      });
      formData.append('file_keys', JSON.stringify(file_keys));
      
      const response = await api.post(`/lms/projects/media/${itemId}/`, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            // You can handle progress updates here if needed
          },
        }
      );
      
      // Refresh data based on type
      if (type === 'configuration' && configurationData && configurationData.id === itemId) {
        try {
          const refreshedConfig = await fetchConfiguration(configurationData.project, itemId);
          setConfigurationData(refreshedConfig);
        } catch (refreshError) {
          console.warn('Failed to refresh configuration data after media upload:', refreshError);
        }
      } else if (type === 'project' && projectData && projectData.id === itemId) {
        try {
          const refreshedProject = await fetchProjectData(itemId);
          setProjectData(refreshedProject);
        } catch (refreshError) {
          console.warn('Failed to refresh project data after media upload:', refreshError);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error uploading attachments:', error);
      throw error;
    }
  };

  const deleteAttachment = async (upload_type, itemId, payload) => {
    try {
      console.log('deleteAttachment', upload_type, itemId, payload);
      const response = await api.post(`/lms/projects/media/${itemId}/delete/`, payload);
      
      // Refresh data based on type
      if (upload_type === 'configuration' && configurationData && configurationData.id === itemId) {
        try {
          const refreshedConfig = await fetchConfiguration(configurationData.project, itemId);
          setConfigurationData(refreshedConfig);
        } catch (refreshError) {
          console.warn('Failed to refresh configuration data after document deletion:', refreshError);
        }
      } else if (upload_type === 'project' && projectData && projectData.id === itemId) {
        try {
          const refreshedProject = await fetchProjectData(itemId);
          setProjectData(refreshedProject);
        } catch (refreshError) {
          console.warn('Failed to refresh project data after document deletion:', refreshError);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error deleting attachments:', error);
      throw error;
    }
  };

  const fetchUnits = async (configId) => {
    showLoader();
    try {
      const response = await api.get(`/lms/configurations/${configId}/units/`);
      setConfigutationUnits(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching units:', error);
      throw error;
    } finally {
      hideLoader();
    }
  };

  const createUnit = async (configId, data) => {
    try {
      const response = await api.post(`/lms/configurations/${configId}/units/`, data);
      setConfigutationUnits(prevUnits => {
        const currentUnits = Array.isArray(prevUnits) ? prevUnits : [];
        return [...currentUnits, response.data];
      });
      return response
    } catch (error) {
      console.error('Error creating unit:', error);
      throw error;
    }
  };

  const updateUnit = async (configId, unitId, data) => {
    try {
      const response = await api.put(`/lms/configurations/${configId}/units/${unitId}/`, data);
      setConfigutationUnits(prevUnits => {
        const currentUnits = Array.isArray(prevUnits) ? prevUnits : [];
        return currentUnits.map(unit => 
          unit.id === unitId ? response.data : unit
        );
      });
      return response; // Return full response object instead of just data
    } catch (error) {
      console.error('Error updating unit:', error);
      throw error;
    }
  };

  const deleteUnit = async (configId, unitId) => {
    showLoader();
    try {
      console.log(`Deleting unit ${unitId} from configuration ${configId}`);
      const response = await api.delete(`/lms/configurations/${configId}/units/${unitId}/`);
      console.log('Delete unit API response:', response);
      
      // Update local state
      setConfigutationUnits(prevUnits => {
        const currentUnits = Array.isArray(prevUnits) ? prevUnits : [];
        return currentUnits.filter(unit => unit.id !== unitId);
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting unit:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      // If the error response contains success message, treat it as success
      if (error.response && error.response.status === 200 && error.response.data?.detail === "Unit deleted successfully") {
        console.log('Treating as success despite error structure');
        // Update local state
        setConfigutationUnits(prevUnits => {
          const currentUnits = Array.isArray(prevUnits) ? prevUnits : [];
          return currentUnits.filter(unit => unit.id !== unitId);
        });
        
        // Return a mock success response
        return {
          status: 200,
          data: { detail: "Unit deleted successfully" }
        };
      }
      
      throw error;
    } finally {
      hideLoader();
    }
  };

  // Memoize the fetchUnitById function using useCallback
  const fetchUnitById = useCallback(async (configId, unitId) => {
    if (!configId || !unitId) {
      console.error("Missing configId or unitId in fetchUnitById");
      return null;
    }
    
    try {
      showLoader();
      console.log(`Fetching unit with configId: ${configId}, unitId: ${unitId}`);
      
      const response = await api.get(`/lms/configurations/${configId}/units/${unitId}/`);
      
      console.log("API response:", response);
      
      if (response.data) {
        // Make sure we're updating the currentUnit state
        setCurrentUnit(response.data);
        return response.data;
      } else {
        console.error("No data returned from API");
        return null;
      }
    } catch (error) {
      console.error('Error fetching unit details:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      return null;
    } finally {
      hideLoader();
    }
  }, []);

  const updateBaseCommission = async (projectId, baseCommission) => {
    try {
      const response = await api.patch(`/lms/projects/${projectId}/commission/`, { base_commission: baseCommission });
      return response;
    } catch (error) {
      console.error('Error updating base commission:', error);
      return error;
    }
  };


  const importConfigurations = async (projectId, data) => {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      
      if (!data || !data.configurations || !Array.isArray(data.configurations)) {
        throw new Error('Invalid data format. Expected configurations array.');
      }
      
      if (data.configurations.length === 0) {
        throw new Error('No configurations to import');
      }
      
      const response = await api.post(`/lms/projects/${projectId}/configurations/csv-import/`, data);
      return response.data;
    } catch (error) {
      console.error('Error importing configurations:', error);
      
      // Re-throw the error with additional context if needed
      if (error.response) {
        // Server error - pass through as is
        throw error;
      } else if (error.request) {
        // Network error
        throw new Error('Network error. Please check your internet connection.');
      } else {
        // Other error (like validation errors we added)
        throw error;
      }
    }
  };

  const importUnits = async (configurationId, data) => {
    try {
      if (!configurationId) {
        throw new Error('Configuration ID is required');
      }
      
      if (!data || !data.units || !Array.isArray(data.units)) {
        throw new Error('Invalid data format. Expected units array.');
      }
      
      if (data.units.length === 0) {
        throw new Error('No units to import');
      }
      
      const response = await api.post(`/lms/configurations/${configurationId}/units/csv-import/`, data);
      return response.data;
    } catch (error) {
      console.error('Error importing units:', error);
      
      // Re-throw the error with additional context if needed
      if (error.response) {
        // Server error - pass through as is
        throw error;
      } else if (error.request) {
        // Network error
        throw new Error('Network error. Please check your internet connection.');
      } else {
        // Other error (like validation errors we added)
        throw error;
      }
    }
  };

  // Value to be provided by the context
  const value = {
    // State
    projects,
    projectData,
    filterOptions,
    appliedFilters,
    defaultFilterOptions,
    developers,
    currentProject,
    selectionOptions,
    setSelectionOptions,
    configurations,
    configurationData,
    configutationUnits,
    unitData,
    currentUnit,
    // Functions
    setAppliedFilters,
    fetchProjects,
    fetchProjectData,
    fetchFilterOptions,
    fetchDefaultFilterOptions,
    resetFilters,
    createProject,
    updateProjectData,
    deleteProject,
    fetchDevelopers,
    fetchSelectionOptions,
    fetchConfigurations,
    fetchConfiguration,
    createConfiguration,
    updateConfiguration,
    deleteConfiguration,
    uploadAttachment,
    deleteAttachment,
    fetchUnits,
    createUnit,
    updateUnit,
    deleteUnit,
    fetchUnitById,
    updateBaseCommission,
    importConfigurations,
    importUnits,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export default ProjectContext;
